import { Head } from 'vite-react-ssg'
import { useLocation } from 'react-router-dom'
import {
  buildSeoUrls,
  normalizeMetaDescription,
  normalizeSeoTitle,
} from '@/lib/seo'

interface SEOHeadProps {
  title: string;
  description: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageType?: string;
  ogType?: "website" | "article";
  noIndex?: boolean;
  jsonLd?: object | object[];
}

const SITE_URL = "https://dociohealth.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export const SEOHead = ({
  title,
  description,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt = "Docio Health - On-demand medical care from licensed doctors",
  ogImageWidth = 1200,
  ogImageHeight = 630,
  ogImageType = "image/png",
  ogType = "website",
  noIndex = false,
  jsonLd,
}: SEOHeadProps) => {
  const { pathname } = useLocation();
  const { canonicalUrl, alternates } = buildSeoUrls(pathname);
  const fullTitle = normalizeSeoTitle(title);
  const metaDescription = normalizeMetaDescription(description);

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <link rel="canonical" href={canonicalUrl} />
      )}

      {noIndex
        ? null
        : alternates.map(({ locale, href }) => (
            <link
              key={locale}
              rel="alternate"
              hrefLang={locale}
              href={href}
            />
          ))}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      <meta property="og:image:type" content={ogImageType} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:site_name" content="Docio Health" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@docio_health" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* JSON-LD Structured Data */}
      {jsonLd ? (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd])}
        </script>
      ) : null}
    </Head>
  );
};
